import { UserContext } from 'core/userContext/types';
import { Bell } from 'ui/icons/alert/Bell';
import { Chat } from 'ui/icons/action/Chat';
import { Help } from 'ui/icons/action/Help';
import { QuickActions24 } from 'ui/icons/aurora/16px/cmdk/QuickActions24';
import { Settings } from 'ui/icons/action/Settings';
import { ORG_PERMISSIONS, PROJECT_PERMISSIONS } from 'utils/permissions';
import { ProfileAvatar } from '../components/ProfileAvatar';
import {
  NAVBAR_MENU_IDS,
  NAVBAR_MENU_TOOLTIPS,
  NAVIGATION_ITEMS,
  SLIDER_MENUS,
  SUPPORT_LINKS,
} from '../constants';
import { NavbarDropdownGroup, NavbarOption, NavbarProjectSettings } from '../types';
import { filterLinksByAccessible } from './navlinks';

/**
 * Creates main navigation options for Global Nav based on user context, permissions, and feature flags
 * Provides an optional override to modify/remove certain properties for different contexts
 */
export const createNavbarLinks = (
  userContext: UserContext,
  settings: NavbarProjectSettings,
  optionOverrides: Partial<NavbarOption> = {},
): NavbarDropdownGroup[] => {
  const { accountType, featureFlags, rbacPermissions } = userContext;

  const formatted = NAVIGATION_ITEMS.map((item) => {
    const { dropdownOptions } = item;

    const filtered = filterLinksByAccessible({
      links: dropdownOptions,
      accountType,
      featureFlags,
      rbacPermissions,
      settings,
    });

    const options = filtered.map((opt) => ({ ...opt, ...optionOverrides }));

    return {
      ...item,
      dropdownOptions: options,
    };
  });

  /**
   * dropdownOptions may be empty, if the user does not have access to any of the
   * relevant links. We want to filter those out so we don't show empty dropdowns.
   */
  return formatted.filter((item) => item.dropdownOptions.length > 0);
};

/** Creates navigation to toggle a drawer for Settings, Notifications and Account */
export const createNavbarMenus = (userContext: Partial<UserContext>) => {
  const {
    accountType,
    avatarUrl,
    featureFlags = {},
    firstName,
    lastName,
    project,
    rbacPermissions,
  } = userContext;
  const supportLinks = filterLinksByAccessible({
    accountType,
    featureFlags,
    links: SUPPORT_LINKS,
    rbacPermissions,
  });

  const menus = [
    {
      featureFlags: ['SupportChatTest'],
      id: NAVBAR_MENU_IDS.chat,
      Icon: () => <Chat height={20} width={20} />,
      navKey: SLIDER_MENUS.OPEN_CHAT,
      tooltip: NAVBAR_MENU_TOOLTIPS.chat,
    },
    {
      dropdownOptions: supportLinks,
      id: NAVBAR_MENU_IDS.support,
      Icon: () => <Help height={24} width={24} />,
      navKey: SLIDER_MENUS.OPEN_SUPPORT,
      tooltip: NAVBAR_MENU_TOOLTIPS.support,
    },
    {
      featureFlags: ['QuickActionsMenu'],
      id: NAVBAR_MENU_IDS.quickActions,
      Icon: () => <QuickActions24 height={22} width={22} />,
      navKey: SLIDER_MENUS.OPEN_QUICK_ACTIONS,
      tooltip: NAVBAR_MENU_TOOLTIPS.quickActions,
    },
    {
      id: NAVBAR_MENU_IDS.settings,
      Icon: () => <Settings height={24} width={24} />,
      navKey: SLIDER_MENUS.OPEN_SETTINGS,
      tooltip: NAVBAR_MENU_TOOLTIPS.settings,
      childPermissions: [
        PROJECT_PERMISSIONS.ManageProjectSettings,
        PROJECT_PERMISSIONS.ViewChannelConfiguration,
        ORG_PERMISSIONS.ManageMembers,
        ORG_PERMISSIONS.ManageRoles,
        ORG_PERMISSIONS.ManageBilling,
      ],
    },
    {
      id: NAVBAR_MENU_IDS.notifications,
      Icon: Bell,
      navKey: SLIDER_MENUS.OPEN_NOTIFICATION_CENTER,
      tooltip: NAVBAR_MENU_TOOLTIPS.notifications,
    },
    {
      id: NAVBAR_MENU_IDS.account,
      Icon: () => <ProfileAvatar userContext={{ firstName, lastName, avatarUrl, project }} />,
      navKey: SLIDER_MENUS.OPEN_ACCOUNTS_AND_PROJECTS,
      tooltip: NAVBAR_MENU_TOOLTIPS.account,
    },
  ];

  const menusWithPermission = menus.filter((menu) => {
    const { featureFlags: menuFeatureFlags = [], childPermissions } = menu;
    const hasRequiredFeatureFlags = menuFeatureFlags?.every((flag) => Boolean(featureFlags[flag]));
    /**
     * If there are permissions associated with the menu options, at least one
     * menu option should be available to the user to show the menu.
     */
    const canAcessAtLeastOneMenuOption =
      !childPermissions ||
      rbacPermissions?.some((permission) => childPermissions?.includes(permission));
    return hasRequiredFeatureFlags && canAcessAtLeastOneMenuOption;
  });

  return menusWithPermission;
};
